/**
 * QR Code Encoder
 * Main factory function for creating QR codes
 */

import { createLogger } from "@/domain/logging";
import { autoDetectTypeNumber } from "./auto-detect-type-number";
import { qrBitBuffer } from "./bit-buffer";
import { composeSvgAccessibilityAttr } from "./compose-svg-accessibility-attr";
import { qr8BitByte, qrAlphaNum, qrKanji, qrNumber } from "./data-modes";
import { computeBlockECData, interleaveCodewords } from "./ec-codewords";
import { createDataURL } from "./output";
import { padDataBuffer } from "./pad-data-buffer";
import { placeDataBitInModule } from "./place-data-bit-in-module";
import { getRSBlocks } from "./rs-blocks";
import type {
  QRBitBuffer as QRBitBufferType,
  QRCodeInstance,
  QRDataSegment,
  QRErrorCorrectionLevelChar,
} from "./types";
import { QRErrorCorrectionLevel } from "./types";
import {
  getBCHTypeInfo,
  getBCHTypeNumber,
  getLengthInBits,
  getLostPoint,
  getMaskFunction,
  getPatternPosition,
} from "./utils";

const _log = createLogger("ENCODER");

/**
 * Create a QR code instance
 * @param typeNumber QR version (1-40), 0 for auto
 * @param errorCorrectionLevel Error correction level ('L', 'M', 'Q', 'H')
 */
export function qrcode(
  typeNumber: number,
  errorCorrectionLevel: QRErrorCorrectionLevelChar,
): QRCodeInstance {
  let _typeNumber = typeNumber;
  const _errorCorrectionLevel = QRErrorCorrectionLevel[errorCorrectionLevel];
  let _modules: (boolean | null)[][] | null = null;
  let _moduleCount = 0;
  let _dataCache: number[] | null = null;
  const _dataList: QRDataSegment[] = [];

  const makeImpl = (test: boolean, maskPattern: number): void => {
    _moduleCount = _typeNumber * 4 + 17;
    _modules = ((): (boolean | null)[][] => {
      const modules: (boolean | null)[][] = Array.from({ length: _moduleCount });
      for (let row = 0; row < _moduleCount; row += 1) {
        modules[row] = Array.from({ length: _moduleCount });
        for (let col = 0; col < _moduleCount; col += 1) {
          modules[row][col] = null;
        }
      }
      return modules;
    })();

    setupPositionProbePattern(0, 0);
    setupPositionProbePattern(_moduleCount - 7, 0);
    setupPositionProbePattern(0, _moduleCount - 7);
    setupPositionAdjustPattern();
    setupTimingPattern();
    setupTypeInfo(test, maskPattern);

    if (_typeNumber >= 7) {
      setupTypeNumber(test);
    }

    _dataCache ??= createData(_typeNumber, _errorCorrectionLevel, _dataList);

    mapData(_dataCache, maskPattern);
  };

  const setupPositionProbePattern = (row: number, col: number): void => {
    for (let r = -1; r <= 7; r += 1) {
      if (row + r <= -1 || row + r >= _moduleCount) continue;

      for (let c = -1; c <= 7; c += 1) {
        if (col + c <= -1 || col + c >= _moduleCount) continue;

        if (
          (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
          (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
          (r >= 2 && r <= 4 && c >= 2 && c <= 4)
        ) {
          _modules![row + r][col + c] = true;
        } else {
          _modules![row + r][col + c] = false;
        }
      }
    }
  };

  const getBestMaskPattern = (): number => {
    let minLostPoint = 0;
    let pattern = 0;

    for (let i = 0; i < 8; i += 1) {
      makeImpl(true, i);

      const lostPoint = getLostPoint(_this);

      if (i === 0 || minLostPoint > lostPoint) {
        minLostPoint = lostPoint;
        pattern = i;
      }
    }

    return pattern;
  };

  const setupTimingPattern = (): void => {
    for (let r = 8; r < _moduleCount - 8; r += 1) {
      if (_modules![r][6] != null) continue;
      _modules![r][6] = r % 2 === 0;
    }

    for (let c = 8; c < _moduleCount - 8; c += 1) {
      if (_modules![6][c] != null) continue;
      _modules![6][c] = c % 2 === 0;
    }
  };

  const fillAlignmentModule = (row: number, col: number): void => {
    for (let r = -2; r <= 2; r += 1) {
      for (let c = -2; c <= 2; c += 1) {
        _modules![row + r][col + c] =
          r === -2 || r === 2 || c === -2 || c === 2 || (r === 0 && c === 0);
      }
    }
  };

  const setupPositionAdjustPattern = (): void => {
    const pos = getPatternPosition(_typeNumber);

    for (const row of pos) {
      for (const col of pos) {
        if (_modules![row][col] != null) continue;

        fillAlignmentModule(row, col);
      }
    }
  };

  const setupTypeNumber = (test: boolean): void => {
    const bits = getBCHTypeNumber(_typeNumber);

    for (let i = 0; i < 18; i += 1) {
      const mod = !test && ((bits >> i) & 1) === 1;
      _modules![Math.floor(i / 3)][(i % 3) + _moduleCount - 8 - 3] = mod;
    }

    for (let i = 0; i < 18; i += 1) {
      const mod = !test && ((bits >> i) & 1) === 1;
      _modules![(i % 3) + _moduleCount - 8 - 3][Math.floor(i / 3)] = mod;
    }
  };

  const setupTypeInfo = (test: boolean, maskPattern: number): void => {
    const data = (_errorCorrectionLevel << 3) | maskPattern;
    const bits = getBCHTypeInfo(data);

    // vertical
    for (let i = 0; i < 15; i += 1) {
      const mod = !test && ((bits >> i) & 1) === 1;

      if (i < 6) {
        _modules![i][8] = mod;
      } else if (i < 8) {
        _modules![i + 1][8] = mod;
      } else {
        _modules![_moduleCount - 15 + i][8] = mod;
      }
    }

    // horizontal
    for (let i = 0; i < 15; i += 1) {
      const mod = !test && ((bits >> i) & 1) === 1;

      if (i < 8) {
        _modules![8][_moduleCount - i - 1] = mod;
      } else if (i < 9) {
        _modules![8][15 - i - 1 + 1] = mod;
      } else {
        _modules![8][15 - i - 1] = mod;
      }
    }

    // fixed module
    _modules![_moduleCount - 8][8] = !test;
  };

  const mapData = (data: number[], maskPattern: number): void => {
    let inc = -1;
    let row = _moduleCount - 1;
    const maskFunc = getMaskFunction(maskPattern);
    const state = { bitIndex: 7, byteIndex: 0 };

    let col = _moduleCount - 1;
    while (col > 0) {
      if (col === 6) col -= 1;

      while (true) {
        for (let c = 0; c < 2; c += 1) {
          placeDataBitInModule(_modules!, row, col - c, data, maskFunc, state);
        }

        row += inc;

        if (row < 0 || _moduleCount <= row) {
          row -= inc;
          inc = -inc;
          break;
        }
      }
      col -= 2;
    }
  };

  const createBytes = (
    buffer: QRBitBufferType,
    rsBlocks: ReturnType<typeof getRSBlocks>,
  ): number[] => {
    let offset = 0;
    let maxDcCount = 0;
    let maxEcCount = 0;
    const dcdata: number[][] = Array.from({ length: rsBlocks.length });
    const ecdata: number[][] = Array.from({ length: rsBlocks.length });

    for (let r = 0; r < rsBlocks.length; r += 1) {
      const result = computeBlockECData(buffer, rsBlocks[r], offset);
      dcdata[r] = result.dcdata;
      ecdata[r] = result.ecdata;
      offset = result.newOffset;
      maxDcCount = Math.max(maxDcCount, rsBlocks[r].dataCount);
      maxEcCount = Math.max(maxEcCount, rsBlocks[r].totalCount - rsBlocks[r].dataCount);
    }

    let totalCodeCount = 0;
    for (const block of rsBlocks) {
      totalCodeCount += block.totalCount;
    }

    return interleaveCodewords(dcdata, ecdata, maxDcCount, maxEcCount, totalCodeCount);
  };

  const createData = (
    typeNumber: number,
    errorCorrectionLevel: number,
    dataList: QRDataSegment[],
  ): number[] => {
    const rsBlocks = getRSBlocks(typeNumber, errorCorrectionLevel);

    const buffer = qrBitBuffer();

    for (const data of dataList) {
      buffer.put(data.getMode(), 4);
      buffer.put(data.getLength(), getLengthInBits(data.getMode(), typeNumber));
      data.write(buffer);
    }

    // calc num max data
    let totalDataCount = 0;
    for (const block of rsBlocks) {
      totalDataCount += block.dataCount;
    }

    if (buffer.getLengthInBits() > totalDataCount * 8) {
      throw new Error(`code length overflow. (${buffer.getLengthInBits()}>${totalDataCount * 8})`);
    }

    padDataBuffer(buffer, totalDataCount);

    return createBytes(buffer, rsBlocks);
  };

  const escapeXml = (s: string): string => {
    let escaped = "";
    for (let i = 0; i < s.length; i += 1) {
      const c = s.charAt(i);
      switch (c) {
        case "<":
          escaped += "&lt;";
          break;
        case ">":
          escaped += "&gt;";
          break;
        case "&":
          escaped += "&amp;";
          break;
        case '"':
          escaped += "&quot;";
          break;
        default:
          escaped += c;
          break;
      }
    }
    return escaped;
  };

  const _isHalfDark = (x: number, y: number, min: number, max: number, row: number): boolean => {
    return (
      min <= x && x < max && min <= y && y < max && _this.isDark(row, Math.floor((x - min) / 1))
    );
  };

  const _buildHalfRow = (
    y: number,
    size: number,
    min: number,
    max: number,
    marginVal: number,
    blocks: Record<string, string>,
    blocksLastLineNoMargin: Record<string, string>,
  ): string => {
    const r1 = Math.floor((y - min) / 1);
    const r2 = Math.floor((y + 1 - min) / 1);
    let row = "";
    for (let x = 0; x < size; x += 1) {
      let p = _isHalfDark(x, y, min, max, r1) ? " " : "█";
      p += _isHalfDark(x, y + 1, min, max, r2) ? " " : "█";
      row += marginVal < 1 && y + 1 >= max ? blocksLastLineNoMargin[p] : blocks[p];
    }
    return row;
  };

  const _createHalfASCII = (margin?: number): string => {
    const cellSize = 1;
    const marginVal = margin ?? cellSize * 2;

    const size = _moduleCount * cellSize + marginVal * 2;
    const min = marginVal;
    const max = size - marginVal;

    const blocks: Record<string, string> = {
      "██": "█",
      "█ ": "▀",
      " █": "▄",
      "  ": " ",
    };

    const blocksLastLineNoMargin: Record<string, string> = {
      "██": "▀",
      "█ ": "▀",
      " █": " ",
      "  ": " ",
    };

    let ascii = "";
    for (let y = 0; y < size; y += 2) {
      ascii += _buildHalfRow(y, size, min, max, marginVal, blocks, blocksLastLineNoMargin);
      ascii += "\n";
    }

    if (size % 2 && marginVal > 0) {
      return ascii.substring(0, ascii.length - size - 1) + "▀".repeat(size);
    }

    return ascii.substring(0, ascii.length - 1);
  };

  const _this: QRCodeInstance = {
    addData: (data: string, mode = "Byte"): void => {
      let newData: QRDataSegment | null = null;

      switch (mode) {
        case "Numeric":
          newData = qrNumber(data);
          break;
        case "Alphanumeric":
          newData = qrAlphaNum(data);
          break;
        case "Byte":
          newData = qr8BitByte(data);
          break;
        case "Kanji":
          newData = qrKanji(data);
          break;
        default:
          throw new Error(`mode:${mode}`);
      }

      _dataList.push(newData);
      _dataCache = null;
    },

    isDark: (row: number, col: number): boolean => {
      if (row < 0 || _moduleCount <= row || col < 0 || _moduleCount <= col) {
        throw new Error(`${row},${col}`);
      }
      return _modules![row][col] === true;
    },

    getModuleCount: () => _moduleCount,

    make: (maskPattern?: number): void => {
      if (_typeNumber < 1) {
        _typeNumber = autoDetectTypeNumber(_errorCorrectionLevel, _dataList);
      }

      // Use specified mask pattern (0-7) or auto-select best one
      const finalMaskPattern =
        typeof maskPattern === "number" && maskPattern >= 0 && maskPattern <= 7
          ? maskPattern
          : getBestMaskPattern();
      makeImpl(false, finalMaskPattern);
    },

    createTableTag: (cellSize?: number, margin?: number): string => {
      const cellSizeVal = cellSize || 2;
      const marginVal = margin ?? cellSizeVal * 4;

      let qrHtml = "";

      qrHtml += '<table style="';
      qrHtml += " border-width: 0px; border-style: none;";
      qrHtml += " border-collapse: collapse;";
      qrHtml += ` padding: 0px; margin: ${marginVal}px;`;
      qrHtml += '">';
      qrHtml += "<tbody>";

      for (let r = 0; r < _this.getModuleCount(); r += 1) {
        qrHtml += "<tr>";

        for (let c = 0; c < _this.getModuleCount(); c += 1) {
          qrHtml += '<td style="';
          qrHtml += " border-width: 0px; border-style: none;";
          qrHtml += " border-collapse: collapse;";
          qrHtml += " padding: 0px; margin: 0px;";
          qrHtml += ` width: ${cellSizeVal}px;`;
          qrHtml += ` height: ${cellSizeVal}px;`;
          qrHtml += " background-color: ";
          qrHtml += _this.isDark(r, c) ? "#000000" : "#ffffff";
          qrHtml += ";";
          qrHtml += '"/>';
        }

        qrHtml += "</tr>";
      }

      qrHtml += "</tbody>";
      qrHtml += "</table>";

      return qrHtml;
    },

    createSvgTag: (
      cellSize?:
        | number
        | {
            cellSize?: number;
            margin?: number;
            alt?: unknown;
            title?: unknown;
            scalable?: boolean;
          },
      margin?: number,
      alt?: unknown,
      title?: unknown,
    ): string => {
      let opts: {
        cellSize?: number;
        margin?: number;
        alt?: unknown;
        title?: unknown;
        scalable?: boolean;
      } = {};
      if (typeof cellSize === "object") {
        opts = cellSize;
        cellSize = opts.cellSize;
        margin = opts.margin;
        alt = opts.alt;
        title = opts.title;
      }

      const cellSizeVal = cellSize || 2;
      const marginVal = margin ?? cellSizeVal * 4;

      const altObj = composeSvgAccessibilityAttr(alt, "qrcode-description");
      const titleObj = composeSvgAccessibilityAttr(title, "qrcode-title");

      const size = _this.getModuleCount() * cellSizeVal + marginVal * 2;
      const rect = `l${cellSizeVal},0 0,${cellSizeVal} -${cellSizeVal},0 0,-${cellSizeVal}z `;

      let qrSvg = "";
      qrSvg += '<svg version="1.1" xmlns="http://www.w3.org/2000/svg"';
      qrSvg += opts.scalable ? "" : ` width="${size}px" height="${size}px"`;
      qrSvg += ` viewBox="0 0 ${size} ${size}" `;
      qrSvg += ' preserveAspectRatio="xMinYMin meet"';
      qrSvg +=
        titleObj.text || altObj.text
          ? ` role="img" aria-labelledby="${escapeXml([titleObj.id, altObj.id].join(" ").trim())}"`
          : "";
      qrSvg += ">";
      qrSvg += titleObj.text
        ? `<title id="${escapeXml(titleObj.id || "")}">${escapeXml(titleObj.text)}</title>`
        : "";
      qrSvg += altObj.text
        ? `<description id="${escapeXml(altObj.id || "")}">${escapeXml(altObj.text)}</description>`
        : "";
      qrSvg += '<rect width="100%" height="100%" fill="white" cx="0" cy="0"/>';
      qrSvg += '<path d="';

      for (let r = 0; r < _this.getModuleCount(); r += 1) {
        const mr = r * cellSizeVal + marginVal;
        for (let c = 0; c < _this.getModuleCount(); c += 1) {
          if (_this.isDark(r, c)) {
            const mc = c * cellSizeVal + marginVal;
            qrSvg += `M${mc},${mr}${rect}`;
          }
        }
      }

      qrSvg += '" stroke="transparent" fill="black"/>';
      qrSvg += "</svg>";

      return qrSvg;
    },

    createDataURL: (cellSize?: number, margin?: number): string => {
      const cellSizeVal = cellSize || 2;
      const marginVal = margin ?? cellSizeVal * 4;

      const size = _this.getModuleCount() * cellSizeVal + marginVal * 2;
      const min = marginVal;
      const max = size - marginVal;

      return createDataURL(size, size, (x, y) => {
        if (min <= x && x < max && min <= y && y < max) {
          const c = Math.floor((x - min) / cellSizeVal);
          const r = Math.floor((y - min) / cellSizeVal);
          return _this.isDark(r, c) ? 0 : 1;
        } else {
          return 1;
        }
      });
    },

    createImgTag: (cellSize?: number, margin?: number, alt?: string): string => {
      const cellSizeVal = cellSize || 2;
      const marginVal = margin ?? cellSizeVal * 4;

      const size = _this.getModuleCount() * cellSizeVal + marginVal * 2;

      let img = "";
      img += "<img";
      img += '\u0020src="';
      img += _this.createDataURL(cellSizeVal, marginVal);
      img += '"';
      img += '\u0020width="';
      img += size;
      img += '"';
      img += '\u0020height="';
      img += size;
      img += '"';
      if (alt) {
        img += '\u0020alt="';
        img += escapeXml(alt);
        img += '"';
      }
      img += "/>";

      return img;
    },

    createASCII: (cellSize?: number, margin?: number): string => {
      const cellSizeVal = cellSize || 1;

      if (cellSizeVal < 2) {
        return _createHalfASCII(margin);
      }

      const cs = cellSizeVal - 1;
      const marginVal = margin ?? cs * 2;

      const size = _moduleCount * cs + marginVal * 2;
      const min = marginVal;
      const max = size - marginVal;

      const white = "██".repeat(cs);
      const black = "  ".repeat(cs);

      let ascii = "";
      for (let y = 0; y < size; y += 1) {
        const r = Math.floor((y - min) / cs);
        let line = "";
        for (let x = 0; x < size; x += 1) {
          let p = 1;

          if (
            min <= x &&
            x < max &&
            min <= y &&
            y < max &&
            _this.isDark(r, Math.floor((x - min) / cs))
          ) {
            p = 0;
          }

          line += p ? white : black;
        }

        for (let i = 0; i < cs; i += 1) {
          ascii += `${line}\n`;
        }
      }

      return ascii.substring(0, ascii.length - 1);
    },

    renderTo2dContext: (context: CanvasRenderingContext2D, cellSize = 2): void => {
      const length = _this.getModuleCount();
      for (let row = 0; row < length; row++) {
        for (let col = 0; col < length; col++) {
          context.fillStyle = _this.isDark(row, col) ? "black" : "white";
          context.fillRect(col * cellSize, row * cellSize, cellSize, cellSize);
        }
      }
    },

    // Debug/test helper to access interleaved data bytes
    getDataCache: (): number[] => {
      if (_typeNumber < 1) {
        throw new Error("QR code not initialized: call make() before getDataCache()");
      }
      _dataCache ??= createData(_typeNumber, _errorCorrectionLevel, _dataList);
      return _dataCache;
    },
  };

  return _this;
}
